// Calls — your recent calls (Layer B4), with call-back. The directory moved
// to its own People page (in the nav between Meetings and Calls), so this is
// now just the call log plus the new-call drawer.
//
// Rows come from the server; a call resolving re-fetches so the log reflects
// what just happened.

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { api, CallHistoryEntry } from '../api';
import { useShell } from '../components/AppShell';
import { CallTarget, NewCallDrawer } from '../components/NewCallDrawer';
import { peerSummary } from '../lib/calls';
import { pushToast } from '../store/callStore';
import { clearMissed, useCallsStore } from '../store/callsStore';
import { Avatar } from '../ui/Avatar';
import { IconButton } from '../ui/IconButton';
import { IconCall, IconPlus } from '../ui/icons';

type StartCall = (targets: CallTarget[]) => Promise<void>;

export function PeoplePage() {
  const { me } = useShell();
  const navigate = useNavigate();

  const [calls, setCalls] = useState<CallHistoryEntry[] | null>(null);
  const [callsFailed, setCallsFailed] = useState(false);
  const [picking, setPicking] = useState(false);

  const openPicker = useCallback(() => setPicking(true), []);
  const closePicker = useCallback(() => setPicking(false), []);

  // Opening the page is what "sees" missed calls, so the nav badge clears here.
  useEffect(() => clearMissed(), []);

  const ended = useCallsStore((s) => s.ended?.callID ?? '');
  useEffect(() => {
    api
      .callHistory()
      .then(setCalls)
      .catch(() => setCallsFailed(true));
  }, [ended]);

  const startCall = useCallback<StartCall>(
    async (targets) => {
      try {
        const r = await api.startCall(targets, 'audio');
        navigate(`/call/${r.call.id}`);
      } catch (e) {
        pushToast(e instanceof Error ? e.message : 'Could not start the call', 'error');
      }
    },
    [navigate],
  );

  if (!me) return <Navigate to="/" replace />;
  if (!me.callsEnabled) return <Navigate to="/" replace />;

  return (
    <div className="bz-shell-col">
      <header className="bz-people-header">
        <div className="bz-people-head-left">
          <h1>Calls</h1>
          <IconButton shape="round" label="New call" onClick={openPicker} className="bz-people-new">
            <IconPlus size={17} />
          </IconButton>
        </div>
      </header>
      <RecentCalls rows={calls} failed={callsFailed} onCallBack={startCall} />
      {picking && <NewCallDrawer onClose={closePicker} onStart={startCall} />}
    </div>
  );
}

function RecentCalls({
  rows,
  failed,
  onCallBack,
}: {
  rows: CallHistoryEntry[] | null;
  failed: boolean;
  onCallBack: StartCall;
}) {
  if (failed) return <p className="bz-people-note bz-error-text">Could not load your calls.</p>;
  if (rows === null) return <p className="bz-people-note">Loading…</p>;
  if (rows.length === 0) return <p className="bz-people-note">No calls yet.</p>;

  return (
    <div className="bz-people-rows">
      {rows.map((c) => (
        <CallRow key={c.id} call={c} onCallBack={onCallBack} />
      ))}
    </div>
  );
}

const CallRow = memo(function CallRow({
  call,
  onCallBack,
}: {
  call: CallHistoryEntry;
  onCallBack: StartCall;
}) {
  const [calling, setCalling] = useState(false);
  const targets = useMemo(
    () =>
      call.group && call.peers?.length
        ? call.peers.map((p) => ({ id: p.id, name: p.name }))
        : [{ id: call.peerId, name: call.peerName }],
    [call.group, call.peers, call.peerId, call.peerName],
  );
  const back = useCallback(() => {
    setCalling(true);
    void onCallBack(targets).finally(() => setCalling(false));
  }, [onCallBack, targets]);
  const name = call.group
    ? peerSummary((call.peers ?? []).map((p) => p.name))
    : call.peerName || 'Unknown';
  return (
    <div className="bz-person-row">
      <span className="bz-person-avatar">
        <Avatar name={name} identity={call.peerId} size={40} photoUrl={call.peerAvatar} />
      </span>
      <div className="bz-person-body">
        <div className={call.missed ? 'bz-person-name bz-call-missed' : 'bz-person-name'}>{name}</div>
        <div className="bz-call-meta">
          <span className={`bz-call-arrow bz-call-arrow-${call.direction}`} aria-hidden="true">
            {call.direction === 'in' ? '↙' : '↗'}
          </span>
          {callLabel(call)} · {fmtWhen(call.startedAt)}
        </div>
      </div>
      <button
        type="button"
        className="bz-person-call"
        onClick={back}
        disabled={calling}
        aria-label={`Call ${name} back`}
      >
        <IconCall size={16} />
        Call
      </button>
    </div>
  );
});

function callLabel(c: CallHistoryEntry): string {
  if (c.state === 'busy') return c.direction === 'out' ? 'Busy' : 'Tried while you were busy';
  if (c.missed) return 'Missed';
  if (c.state === 'declined') return c.direction === 'out' ? 'Declined' : 'You declined';
  if (c.state === 'canceled') return 'Canceled';
  if (c.state === 'missed') return 'No answer';
  if (c.durationSec > 0) return fmtDuration(c.durationSec);
  return c.direction === 'in' ? 'Incoming' : 'Outgoing';
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
